import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .fp-page {
    font-family: 'Inter', system-ui, sans-serif;
    min-height: 100vh;
    background: #0a0f1e;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 20px;
    position: relative;
    overflow: hidden;
  }

  .fp-blob {
    position: fixed; border-radius: 50%; filter: blur(80px);
    pointer-events: none; z-index: 0;
  }
  .fp-blob-1 { width: 480px; height: 480px; background: rgba(99,102,241,0.1);  top: -160px; left: -160px; }
  .fp-blob-2 { width: 380px; height: 380px; background: rgba(79,70,229,0.07);  bottom: -100px; right: -100px; }

  .fp-card {
    position: relative; z-index: 1;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 24px;
    padding: 48px 40px;
    width: 100%; max-width: 440px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.4);
    backdrop-filter: blur(18px);
    animation: fpFadeUp 0.45s ease-out both;
  }

  @keyframes fpFadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .fp-logo-img {
    height: 48px;
    width: auto;
    border-radius: 12px;
    margin-bottom: 20px;
    filter: drop-shadow(0 0 20px rgba(99,102,241,0.5));
  }

  .fp-title    { font-size: 1.75rem; font-weight: 900; color: #f1f5f9; letter-spacing: -0.03em; margin: 0 0 6px; }
  .fp-subtitle { font-size: 14px; color: #64748b; margin: 0 0 28px; line-height: 1.6; }

  .fp-alert { border-radius: 12px; padding: 13px 16px; font-size: 14px; margin-bottom: 20px; }
  .fp-alert-error   { background: rgba(239,68,68,0.1);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .fp-alert-success { background: rgba(34,197,94,0.1);  border: 1px solid rgba(34,197,94,0.2);   color: #86efac; }

  .fp-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 20px; }
  .fp-label { font-size: 13px; font-weight: 700; color: #94a3b8; }

  .fp-input {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(99,102,241,0.2);
    border-radius: 10px;
    padding: 13px 16px;
    font-size: 15px; color: #e2e8f0;
    font-family: inherit; outline: none;
    transition: border-color 0.2s, background 0.2s;
    width: 100%; box-sizing: border-box;
  }
  .fp-input::placeholder { color: #334155; }
  .fp-input:focus { border-color: rgba(99,102,241,0.5); background: rgba(99,102,241,0.05); }

  .fp-submit {
    width: 100%; padding: 14px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white; border: none; border-radius: 12px;
    font-size: 15px; font-weight: 700; cursor: pointer;
    font-family: inherit; transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(99,102,241,0.35);
    margin-bottom: 20px;
  }
  .fp-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(99,102,241,0.5); }
  .fp-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .fp-footer-text {
    text-align: center; font-size: 14px; color: #475569; margin: 0;
  }
  .fp-footer-text a { color: #818cf8; text-decoration: none; font-weight: 600; }
  .fp-footer-text a:hover { color: #a5b4fc; }

  @media (max-width: 480px) { .fp-card { padding: 36px 20px; } }
`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setMessage('');
    try {
      const r = await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email });
      setMessage(r.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fp-page">
      <style>{STYLES}</style>
      <div className="fp-blob fp-blob-1" />
      <div className="fp-blob fp-blob-2" />

      <div className="fp-card">
        <img src={new URL('../assets/images/logo.png', import.meta.url).href} alt="RDRTO Logo" className="fp-logo-img" />
        <h1 className="fp-title">Forgot Password?</h1>
        <p className="fp-subtitle">No worries — enter your email and we'll send you a reset link right away.</p>

        {error && <div className="fp-alert fp-alert-error">{error}</div>}
        {message && <div className="fp-alert fp-alert-success">✅ {message}</div>}

        <form onSubmit={handleSubmit}>
          <div className="fp-group">
            <label className="fp-label" htmlFor="fp-email">Email Address</label>
            <input
              id="fp-email"
              type="email"
              className="fp-input"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="fp-submit" disabled={loading}>
            {loading ? '⏳ Sending…' : '📧 Send Reset Link'}
          </button>
        </form>

        <p className="fp-footer-text">
          Remember your password?{' '}<Link to="/login">Sign in</Link>
        </p>
        <p className="fp-footer-text" style={{ marginTop: 8 }}>
          Don't know your email?{' '}<Link to="/forgot-email">Recover email</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
